// The offline-recording rows the session list unions in, and the one
// precedence rule shared by every union that does it.
//
// `OfflineSyncItem` moved here from `src/types/offline-recording.ts` and
// `OfflineLocalSessionListItem` from `src/atoms/offline-recording/sync.ts`
// (both now re-export these declarations) with the combined-sessions core;
// `backfillQueueServerSessionId` moved verbatim from
// `src/utils/offline-recording/backfillQueueServerSessionId.ts`.
//
// ### Why the inert door and not `/data`
//
// The rule names only the two types below and performs no I/O, and it has two
// remaining app-side readers besides the moved core — the session-list union
// and the consolidated-list union. Homing it here lets all three resolve one
// declaration without pulling React Query and the config seam behind a pure
// function.
//
// ### Why the types are declared here rather than imported
//
// Both are the frontend half of a vocabulary whose source of truth is Rust
// (see `OfflineSyncStatus` above them in `types.ts`), and a package may not
// import an app path. Offline recording has no package of its own yet; when it
// gets one these declarations become a type-only cross-package import, exactly
// as `OfflineSyncStatus` will.

import { type OfflineSyncStatus } from './types';

/**
 * A tracked offline recording — the frontend mirror of the Rust
 * `SyncTrackingRow` (the durable sync queue, `#[serde(rename_all =
 * "camelCase")]`). Keep in lockstep with
 * `src-tauri/src/offline_recording/storage/mod.rs`.
 */
export interface OfflineSyncItem {
  localSessionId: string;
  userId: string;
  serverSessionId: string | null;
  localEncryptedPath: string;
  status: OfflineSyncStatus;
  attemptCount: number;
  lastAttemptAt: string | null;
  lastErrorCode: string | null;
  fileSizeBytes: number | null;
  recordedDurationMs?: number | null;
  captureRegion?: string;
  /**
   * The encounter start used by list/order consumers. Bridge-built rows map
   * the durable `createdTimeLocal` here when present; `updatedAt` retains the
   * sync-tracking row's own creation time.
   *
   * Event-built placeholders may temporarily use the event time until the
   * bridge refresh supplies the durable start.
   */
  createdAt: string;
  updatedAt: string;
  /**
   * The original start instant for a consult born offline. `null` for audio
   * recorded into a server session that already existed.
   *
   * Optional because event-built queue rows predate the bridge refresh that
   * reads this durable value.
   */
  createdTimeLocal?: string | null;
  /**
   * How many recordings this consult holds.
   *
   * A consult is ONE queue row however many times it was stopped and resumed —
   * Rust collapses its takes at the read (`collapse_takes_by_session`) so the
   * clinician sees the encounter, not the recordings inside it. This is the only
   * thing that distinguishes a one-part consult from a two-part one.
   *
   * Optional because a queue row can be built from an event payload that
   * predates the field; treat a missing value as `1`.
   */
  recordingCount?: number;
  /**
   * The transcribe/dictate mode Rust persisted for this consult
   * (`offline_sessions.dictation_type`), verbatim.
   *
   * Carried because for a consult that already has a server session the queue
   * row is the only thing that still knows it. A purely local consult gets its
   * mode back from the cold-boot detail stub, which is rebuilt from the same
   * Rust list row; a server-backed one gets no stub — its detail entry belongs
   * to the real server query — and offline that query has nothing to return, so
   * without this the mode is simply absent and every `mode || TRANSCRIBE`
   * default reads a dictate consult as a transcript one.
   *
   * A plain string rather than `TranscribeMode`: it comes back from SQLite, so
   * a row written by an older build is not guaranteed to be a currently-valid
   * member. `toStoredTranscribeMode` is the one place that decides.
   *
   * Optional because a queue row built from an event payload carries no
   * settings; `null` is Rust's "the user never chose".
   */
  dictationType?: string | null;
  /**
   * What the clinician answered on the patient-consent gate
   * (`offline_sessions.is_consent`), verbatim.
   *
   * Carried for the same reason `dictationType` is: a purely local consult gets
   * its answer back from the cold-boot detail stub, which is rebuilt from the
   * same Rust list row; a server-backed one gets no stub — its detail entry
   * belongs to the real server query — and offline that query has nothing to
   * return, so this row is the only place the answer could survive a quit.
   *
   * Narrower than `dictationType` in which consults populate it, though, and
   * the split is on WHEN the gate was answered rather than on what the row
   * looks like now. The column's one writer is gated on `isOfflineLocalSession`,
   * so it holds a value only where the clinician answered while the consult was
   * still local-only. That includes a local-born consult later given a server id
   * by sync — the column was written before the id arrived, and that is the
   * population this field carries back. A consult that was server-backed from
   * birth never reaches that writer: its answer stages into an in-memory atom
   * that dies with the process, so its column stays NULL and this arrives
   * `null`. The field is the transport and it is correct; feeding that second
   * shape is a separate change on the mutation that owns the write.
   *
   * Three-state, and the states are not interchangeable: `null` is Rust's "the
   * gate never ran", `false` is a refusal and a compliance record. What this
   * field must not do is make them indistinguishable in the DATA. The full
   * argument is stated once, on `definedConsent` in the app's
   * `utils/offline-recording/offlineSessionStub` — not linked, because a
   * contract may not reach into the app, but that is where it lives.
   *
   * Optional because a queue row can be built from an event payload that
   * carries no session settings at all.
   */
  isConsent?: boolean | null;
}

/**
 * A local-only offline session that exists before the durable sync queue has a
 * row to hydrate it. Intentionally small: only the fields required to make a
 * stable synthetic list row.
 */
export interface OfflineLocalSessionListItem {
  localSessionId: string;
  serverSessionId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Precedence rule shared by the offline-list unions: use the local shell's
 * `serverSessionId` mapping only while the durable queue row is still missing
 * one. Returns the item unchanged otherwise.
 */
export const backfillQueueServerSessionId = (
  item: OfflineSyncItem,
  localSessionByLocalId: Map<string, OfflineLocalSessionListItem>
): OfflineSyncItem => {
  const localSession = localSessionByLocalId.get(item.localSessionId);
  return !item.serverSessionId && localSession?.serverSessionId
    ? { ...item, serverSessionId: localSession.serverSessionId }
    : item;
};

/** The only field the queued-delete filter reads off a list row. */
type SessionIdentified = { session_id?: string | null };

/**
 * Drop the rows whose session the clinician has deleted while offline and the
 * backend has not accepted yet — the read half of the queued-delete feature.
 *
 * The ids come from the render-time mirror of the Rust `pending_deletes` table
 * (`offlinePendingServerDeleteIdsAtom`). SQLite is the authority; the mirror is
 * a cache, and this function makes no judgement about whether a delete is still
 * owed — it filters exactly the ids it is handed.
 *
 * ### Why every surface applies this AFTER its offline union
 *
 * Each list composes backend rows with synthetic offline-recording rows, and a
 * synthetic row is keyed by `serverSessionId` when it has one
 * ({@link backfillQueueServerSessionId}). So a queued recording that already
 * has a server session can re-introduce an id the backend rows no longer carry.
 * Filtering the backend rows alone would leave that row visible, which is the
 * one thing the criterion forbids.
 *
 * ### Why identity is preserved when nothing matches
 *
 * The result feeds `useMemo` dependency chains and table props on every list
 * surface in the app, and the overwhelmingly common case is an empty mirror. A
 * fresh array each render would re-render every one of them for nothing, so the
 * input array is returned unchanged whenever no row was dropped.
 *
 * A row with no `session_id` is kept: it cannot be the target of a server-side
 * delete, and dropping it would silently hide a row on an unrelated fault.
 *
 * Pure and headless by design — no title, patient link or note text is read, so
 * nothing on this path can reach a log line or an analytics property.
 */
export const withoutPendingServerDeletes = <T extends SessionIdentified>(
  rows: T[],
  pendingServerDeleteIds: readonly string[]
): T[] => {
  // A behaviour-neutral fast path, NOT a guard: the walk below already returns
  // `rows` unchanged for an empty mirror, so deleting this line fails no test
  // by design. It is here because an empty mirror is the overwhelmingly common
  // case and this runs on every render of every list surface in the app.
  if (pendingServerDeleteIds.length === 0) return rows;
  const pending = new Set(pendingServerDeleteIds);

  // One pass, and the copy is allocated lazily — a mirror holding only ids that
  // belong to some OTHER list is the next most common case, and it walks out
  // having allocated nothing and returning the input array unchanged. Identity
  // is preserved on that path because these rows feed `useMemo` dependency
  // chains and table props on every list surface; a fresh array each render
  // would re-render all of them for nothing.
  let kept: T[] | undefined;
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index]!;
    if (row.session_id && pending.has(row.session_id)) {
      kept ??= rows.slice(0, index);
      continue;
    }
    kept?.push(row);
  }
  return kept ?? rows;
};

/** Stable identity for the empty result, so consumers' `useMemo` deps settle. */
const NO_PENDING_SERVER_DELETE_ROWS: readonly never[] = Object.freeze([]);

/**
 * The exact rows {@link withoutPendingServerDeletes} drops, in input order —
 * the complement of the read filter, for the questions the read filter must not
 * be allowed to answer.
 *
 * A queued delete is an intent the backend has not accepted, so the session
 * still exists server-side. Any consumer asking *"does a session claim this
 * record?"* — rather than *"what should the clinician see?"* — has to count
 * those rows, or the filter turns a hidden session into an apparently free one.
 *
 * The live case is EHR appointment linkage: `isEhrAppointmentLinked` matches an
 * appointment against the session list, and reading a filtered list flips a
 * linked appointment to unlinked, resurfacing it as a bookable row that still
 * carries the deleted session's `session_id`. Handing it these rows back keeps
 * the appointment suppressed, which is the truthful state until the server
 * accepts the deletion.
 *
 * Whole rows, not bare ids: the fallback branch of that check matches
 * `ehr_appt_id` against a provider's unique identifier (desktop integrations
 * like Genie always return `session: null`), and an id-only stub would miss it.
 *
 * Pure and headless, like its complement — no title, patient link or note text
 * is read.
 */
export const onlyPendingServerDeletes = <T extends SessionIdentified>(
  rows: T[],
  pendingServerDeleteIds: readonly string[]
): readonly T[] => {
  if (pendingServerDeleteIds.length === 0) return NO_PENDING_SERVER_DELETE_ROWS;
  const pending = new Set(pendingServerDeleteIds);
  const dropped = rows.filter((row) => !!row.session_id && pending.has(row.session_id));
  // Same reason as the complement: an empty result on every render would churn
  // the `useMemo` chains downstream of it.
  return dropped.length === 0 ? NO_PENDING_SERVER_DELETE_ROWS : dropped;
};

/** The direction a session list is ordered by date. */
export type OfflineSessionDateOrder = 'asc' | 'desc';

/** The only field the offline date ordering reads. */
type OfflineCreatedAt = { createdAt: string };

/**
 * Order offline rows by their creation instant before they are unioned into a
 * session list.
 *
 * Every offline union builds its synthetic rows by walking a de-duping `Map`,
 * so without this they land in queue/hydration insertion order rather than by
 * datetime — which is what a fully-offline list (where every row is synthetic)
 * renders top to bottom. Grouping the rows into Today / Yesterday / Older is
 * NOT enough: that only makes each bucket contiguous and leaves the rows inside
 * a bucket in their upstream order.
 *
 * Used by the new session list when date interleaving is disabled: synthetic
 * rows still need deterministic date order before the unchanged backend rows.
 * Date-interleaved unions instead pass their synthetic rows directly to
 * {@link mergeRowsByCreatedAt}, which owns ordering across both sources.
 *
 * Pure and headless: `createdAt` on an offline row is always
 * written by the same Rust/local writer, so a plain `Date.parse` comparison is
 * enough to order them relative to each other and needs no timezone handling.
 *
 * Stable — equal instants keep their upstream order — and an unparseable
 * `createdAt` is treated as the chronological minimum so a malformed row sinks
 * to the bottom of a newest-first list instead of floating to the top.
 *
 * Decorate-sort-undecorate: each row's `createdAt` is parsed ONCE up front
 * rather than inside the comparator, which a comparator-side parse would run
 * O(n log n) times over the whole hydrated queue. `Date.parse` (not the app's
 * `parseTimeStamp`) is deliberate and must stay: `/contracts` is headless and
 * may not reach an app path, and every offline `createdAt` is written by the
 * same local writer in the same format, so a relative comparison between two of
 * them needs no zone detection.
 */
export const orderOfflineRowsByCreatedAt = <T extends OfflineCreatedAt>(
  rows: readonly T[],
  order: OfflineSessionDateOrder = 'desc'
): T[] => {
  const direction = order === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => {
      const time = Date.parse(row.createdAt);
      return { row, index, time, isValid: !Number.isNaN(time) };
    })
    .toSorted((a, b) => {
      if (a.isValid !== b.isValid) return a.isValid ? direction : -direction;
      // `toSorted` is spec-stable, but only decorating with the original index
      // keeps ties in upstream order across the two-invalid case as well.
      if (!a.isValid) return a.index - b.index;
      if (a.time === b.time) return a.index - b.index;
      return (a.time - b.time) * direction;
    })
    .map(({ row }) => row);
};

/**
 * Insert locally-owned rows into a server-ordered session list by timestamp
 * without reordering the server rows themselves.
 *
 * The backend owns the relative order of `baseRows` (including ties and any
 * presentation-specific ordering). Offline rows have no server position, so
 * they are the only rows this helper moves.
 *
 * `baseRows` must already be monotonic for `getCreatedAt` in `order`. The
 * forward walk intentionally does not sort them: preserving backend-relative
 * order is part of this helper's contract. If a caller supplies a
 * non-monotonic base, that order is preserved and only the inserted rows are
 * guaranteed to stay mutually ordered.
 *
 * `getCreatedAt` owns timestamp parsing and returns milliseconds since epoch.
 * Keeping parsing at the caller preserves EHR wall-clock semantics while
 * allowing Heidi timestamps to use the shared UTC parser.
 *
 * Each inserted row lands before the first base row that sorts strictly after
 * it. Inserted rows are sorted first, so each one's landing point is at or past
 * the previous one's, and a single forward walk over `baseRows` places them all:
 * O(B + I log I), with no `splice` shifting the tail.
 *
 * `getStableOrder` preserves a row's prior server position when a locally
 * corrected timestamp ties another server row exactly.
 */
export const mergeRowsByCreatedAt = <T>(
  baseRows: T[],
  insertedRows: readonly T[],
  getCreatedAt: (row: T) => number | null | undefined,
  order: OfflineSessionDateOrder = 'desc',
  getStableOrder?: (row: T) => number | undefined
): T[] => {
  if (insertedRows.length === 0) return baseRows;

  const direction = order === 'asc' ? 1 : -1;
  const toSortable = (row: T) => {
    const createdAt = getCreatedAt(row);
    const time = createdAt ?? Number.NaN;
    return {
      row,
      time,
      isValid: !Number.isNaN(time),
      stableOrder: getStableOrder?.(row),
    };
  };
  const compare = (
    left: ReturnType<typeof toSortable>,
    right: ReturnType<typeof toSortable>
  ): number => {
    if (left.isValid !== right.isValid) return left.isValid ? direction : -direction;
    if (!left.isValid || left.time === right.time) {
      if (left.stableOrder !== undefined && right.stableOrder !== undefined) {
        return left.stableOrder - right.stableOrder;
      }
      if (left.stableOrder !== undefined) return -1;
      if (right.stableOrder !== undefined) return 1;
      return 0;
    }
    return (left.time - right.time) * direction;
  };

  const orderedInsertedRows = insertedRows
    .map((row, index) => ({ ...toSortable(row), index }))
    .toSorted((left, right) => compare(left, right) || left.index - right.index);

  const merged: T[] = [];
  let baseIndex = 0;
  for (const inserted of orderedInsertedRows) {
    while (baseIndex < baseRows.length) {
      const base = toSortable(baseRows[baseIndex] as T);
      if (compare(inserted, base) < 0) break;
      merged.push(base.row);
      baseIndex += 1;
    }
    merged.push(inserted.row);
  }
  return [...merged, ...baseRows.slice(baseIndex)];
};
